import React, { useState } from "react";
import Button from "components/common/controls/Button";
import UserEmailPhoneIdentifierSelector, {
  UserEmailOrPhoneIdentifier,
} from "components/common/UserEmailPhoneIdentifierSelector";
import { useHistory } from "react-router";
import EditorDescriptor from "./EditorDescriptor";
import * as routePath from "shared/constants/RoutePathConstants";
import Checkbox from "components/common/controls/Checkbox";
import {
  getRememberedSignInData,
  setRememberedSignInData,
} from "shared/utility/RememberMeUtility";
import { SignInSignUpOption } from "shared/enums/SignInSignUpOptionEnum";

export interface SignInFormData {
  email: string;
  phone: string;
  phoneCode: string;
  phoneNumberWithCode: string;
  authenticationType: SignInSignUpOption;
}

interface Props {
  onSubmit: (formData: SignInFormData) => void;
}

export default function SignInForm(props: Props) {
  const history = useHistory();
  const rememberedData = getRememberedSignInData();
  const [isRemembered, setIsRemembered] = useState(
    rememberedData.isRemembered || false
  );

  const [state, setState] = useState<SignInFormData>({
    email: rememberedData.email,
    phone: rememberedData.phone,
    phoneCode: rememberedData.phoneCode,
    phoneNumberWithCode: rememberedData.phoneNumberWithCode,
    authenticationType: rememberedData.signInOption,
  });

  const ButtonSeparator = () => {
    const Border = () => (
      <div className="h-4 w-1/2 border-b border-silver"></div>
    );

    return (
      <div className="flex">
        <Border />
        <span className="mx-2 text-silver">or</span>
        <Border />
      </div>
    );
  };

  const handleCreateNewAccountClick = () => {
    history.push(routePath.SIGN_UP);
  };

  const handleSignInOptionChanged = (option: SignInSignUpOption) => {
    setState({ ...state, authenticationType: option });
  };

  const handlePhoneValueChanged = (value: UserEmailOrPhoneIdentifier) => {
    setState({
      ...state,
      phone: value.phone || "",
      phoneCode: value.phoneCode || "",
      phoneNumberWithCode: value.phoneNumberWithCode || "",
      authenticationType: SignInSignUpOption.Phone,
    });
  };

  const handleEmailValueChanged = (value: UserEmailOrPhoneIdentifier) => {
    setState({
      ...state,
      email: value.email || "",
      authenticationType: SignInSignUpOption.Email,
    });
  };

  const handleSubmit = (evt: React.SyntheticEvent) => {
    evt.preventDefault();
    setRememberedSignInData({
      isRemembered: isRemembered,
      email: state.email,
      phone: state.phone,
      phoneCode: state.phoneCode,
      phoneNumberWithCode: state.phoneNumberWithCode,
      signInOption: state.authenticationType,
    });
    props.onSubmit(state);
  };

  return (
    <EditorDescriptor
      title="Sign In"
      description="enter your credentials
          to view your API dashboard"
      descriptionWidthClass="w-234px"
    >
      <form onSubmit={handleSubmit}>
        <div>
          <UserEmailPhoneIdentifierSelector
            defaultSelection={rememberedData.signInOption}
            label="Sign in with"
            onEmailChanged={handleEmailValueChanged}
            onPhoneChanged={handlePhoneValueChanged}
            email={state.email}
            phoneNumberWithCode={state.phoneNumberWithCode}
            onOptionChanged={handleSignInOptionChanged}
          />
          <div className="mt-5">
            <Checkbox
              name="remember me"
              defaultValue={rememberedData.isRemembered || false}
              labelComponent={<span className="text-sm">Remember me</span>}
              onValueChange={(e: any) => {
                setIsRemembered(e);
              }}
            />
          </div>
        </div>

        <div className="mt-4">
          <Button text="SIGN IN" className="w-full" useSubmitBehavior={true} />
        </div>

        <div className="mt-2">
          <ButtonSeparator />
        </div>

        <div>
          <Button
            variant="secondary"
            text="create an account"
            className="w-full"
            onClick={handleCreateNewAccountClick}
          />
        </div>
      </form>
    </EditorDescriptor>
  );
}
